import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import QuickActions from '../components/QuickActions';
import { useTeam } from '../contexts/TeamContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Save, Music, Home, Users, FileText, Phone, MapPin } from 'lucide-react';

const API_URL = window.location.origin || '';

const Memorial = () => {
  const { currentTeam } = useTeam();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState({
    obituary: [],
    photoBoard: []
  });
  const [preferences, setPreferences] = useState({
    disposition: {
      type: '',
      specific_wishes: '',
      cemetery_owned: '',
      cemetery_name: '',
      cemetery_plot: '',
      cremation_specifics: '',
      urn_preferences: '',
      ash_disposition: '',
      anatomical_donation: '',
      green_burial: ''
    },
    service: {
      service_type: '',
      timing: '',
      location_type: '',
      venue_name: '',
      venue_address: '',
      accessibility_needs: '',
      attendance: '',
      dress_code: '',
      clergy_preference: '',
      clergy_name: '',
      clergy_contact: '',
      religious_tradition: '',
      specific_rituals: ''
    },
    music: {
      ceremony_songs: '',
      background_music: '',
      live_music: '',
      musicians_contact: '',
      songs_to_avoid: ''
    },
    visitation: {
      visitation_preference: '',
      visitation_timing: '',
      visitation_location: '',
      open_closed_casket: '',
      viewing_restrictions: '',
      reception_preference: '',
      reception_location: '',
      reception_food: ''
    },
    memorial_preferences: {
      flowers: '',
      donations_lieu: '',
      memorial_contributions: '',
      dress_attire: '',
      photo_display: '',
      memory_sharing: '',
      video_tribute: '',
      guest_book: ''
    },
    prearrangements: {
      prearranged: '',
      funeral_home: '',
      funeral_home_contact: '',
      contract_number: '',
      prepaid: '',
      insurance_policy: '',
      documents_location: ''
    },
    obituary: {
      draft: '',
      photo_preference: '',
      publications: '',
      online_memorials: '',
      social_media: '',
      uploaded_photo_ids: []
    },
    photo_board: {
      uploaded_photo_ids: [],
      photo_notes: ''
    }
  });

  useEffect(() => {
    if (currentTeam) {
      fetchPreferences();
    }
  }, [currentTeam]);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/teams/${currentTeam.id}/memorial-planning`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data && data.service_preferences) {
          const parsedPrefs = JSON.parse(data.service_preferences);
          setPreferences(prev => ({
            ...prev,
            ...parsedPrefs
          }));

          // Load uploaded photos if they exist
          if (parsedPrefs.obituary?.uploaded_photo_ids) {
            const obituaryPhotos = parsedPrefs.obituary.uploaded_photo_ids.map(id => ({
              id,
              name: 'Photo',
              url: `${API_URL}/api/documents/${id}/download`
            }));
            setUploadedPhotos(prev => ({ ...prev, obituary: obituaryPhotos }));
          }

          if (parsedPrefs.photo_board?.uploaded_photo_ids) {
            const boardPhotos = parsedPrefs.photo_board.uploaded_photo_ids.map(id => ({
              id,
              name: 'Photo',
              url: `${API_URL}/api/documents/${id}/download`
            }));
            setUploadedPhotos(prev => ({ ...prev, photoBoard: boardPhotos }));
          }
        }
      }
    } catch (error) {
      console.error('Error fetching memorial preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/api/teams/${currentTeam.id}/memorial-planning`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          disposition_preference: preferences.disposition.type,
          cemetery_preferences: preferences.disposition.cemetery_name,
          cremation_specifics: preferences.disposition.cremation_specifics,
          service_preferences: JSON.stringify(preferences)
        })
      });

      if (response.ok) {
        toast.success('Memorial preferences saved successfully');
      } else {
        toast.error('Failed to save preferences');
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Error saving preferences');
    } finally {
      setSaving(false);
    }
  };

  const updateSection = (section, field, value) => {
    setPreferences(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handlePhotoUpload = async (event, photoType) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadedIds = [];
      const uploadedUrls = [];

      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${API_URL}/api/teams/${currentTeam.id}/documents`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });

        if (response.ok) {
          const data = await response.json();
          uploadedIds.push(data.id);
          uploadedUrls.push({
            id: data.id,
            name: file.name,
            url: `${API_URL}/api/documents/${data.id}/download`
          });
        }
      }

      // Update state with uploaded photos
      setUploadedPhotos(prev => ({
        ...prev,
        [photoType]: [...prev[photoType], ...uploadedUrls]
      }));

      // Update preferences with photo IDs
      const section = photoType === 'obituary' ? 'obituary' : 'photo_board';
      const field = 'uploaded_photo_ids';
      const currentIds = preferences[section][field] || [];
      updateSection(section, field, [...currentIds, ...uploadedIds]);

      toast.success(`${files.length} photo(s) uploaded successfully`);
    } catch (error) {
      console.error('Error uploading photos:', error);
      toast.error('Error uploading photos');
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (photoType, photoId) => {
    // Remove from uploaded photos display
    setUploadedPhotos(prev => ({
      ...prev,
      [photoType]: prev[photoType].filter(p => p.id !== photoId)
    }));

    // Remove from preferences
    const section = photoType === 'obituary' ? 'obituary' : 'photo_board';
    const field = 'uploaded_photo_ids';
    const currentIds = preferences[section][field] || [];
    updateSection(section, field, currentIds.filter(id => id !== photoId));

    toast.success('Photo removed');
  };

  if (!currentTeam) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto fade-in">
          <h1 className="text-4xl font-bold mb-6">Memorial Planning</h1>
          <Card className="p-8">
            <p className="text-gray-600">Please select a team to manage memorial preferences.</p>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto fade-in">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-4xl font-bold">Memorial Planning</h1>
            <p className="text-gray-600 mt-2">Practical planning for after death - no fear, just preparation</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={savePreferences} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save All'}
            </Button>
            <QuickActions position="top" />
          </div>
        </div>

        {loading ? (
          <Card className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading preferences...</p>
          </Card>
        ) : (
          <Tabs defaultValue="disposition" className="space-y-6">
            <TabsList className="grid grid-cols-4 md:grid-cols-7 gap-2">
              <TabsTrigger value="disposition">
                <MapPin className="w-4 h-4 mr-2" />
                Disposition
              </TabsTrigger>
              <TabsTrigger value="service">
                <Home className="w-4 h-4 mr-2" />
                Service
              </TabsTrigger>
              <TabsTrigger value="music">
                <Music className="w-4 h-4 mr-2" />
                Music
              </TabsTrigger>
              <TabsTrigger value="visitation">
                <Users className="w-4 h-4 mr-2" />
                Visitation
              </TabsTrigger>
              <TabsTrigger value="memorial">
                <FileText className="w-4 h-4 mr-2" />
                Memorial
              </TabsTrigger>
              <TabsTrigger value="prearrangements">
                <Phone className="w-4 h-4 mr-2" />
                Pre-arrangements
              </TabsTrigger>
              <TabsTrigger value="obituary">
                <FileText className="w-4 h-4 mr-2" />
                Obituary
              </TabsTrigger>
            </TabsList>

            {/* Disposition Tab */}
            <TabsContent value="disposition">
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-6">Final Disposition Preferences</h2>
                <p className="text-sm text-gray-600 mb-6">What happens to your body after death</p>
                <div className="space-y-6">
                  <div>
                    <Label>Disposition type *</Label>
                    <select
                      value={preferences.disposition.type}
                      onChange={(e) => updateSection('disposition', 'type', e.target.value)}
                      className="w-full border-2 rounded-md px-3 py-2 mt-1"
                    >
                      <option value="">Select preference...</option>
                      <option value="burial">Burial</option>
                      <option value="cremation">Cremation</option>
                      <option value="anatomical_donation">Anatomical Donation (Medical School/Research)</option>
                      <option value="terramation">Terramation (Human Composting)</option>
                      <option value="aquamation">Aquamation (Water Cremation/Alkaline Hydrolysis)</option>
                      <option value="entombment">Entombment (Mausoleum)</option>
                      <option value="green_burial">Green/Natural Burial</option>
                      <option value="undecided">Not decided yet</option>
                    </select>
                  </div>

                  <div>
                    <Label>Specific wishes or requirements</Label>
                    <Textarea
                      value={preferences.disposition.specific_wishes}
                      onChange={(e) => updateSection('disposition', 'specific_wishes', e.target.value)}
                      placeholder="Any specific details about your disposition preference"
                      rows={3}
                      className="mt-1"
                    />
                  </div>

                  {/* Cemetery Section */}
                  <div className="border-t pt-6 mt-6">
                    <h3 className="text-lg font-semibold mb-4">Cemetery Information</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Do you own cemetery property?</Label>
                        <select
                          value={preferences.disposition.cemetery_owned}
                          onChange={(e) => updateSection('disposition', 'cemetery_owned', e.target.value)}
                          className="w-full border-2 rounded-md px-3 py-2 mt-1"
                        >
                          <option value="">Select...</option>
                          <option value="yes">Yes, I own a plot/space</option>
                          <option value="family_plot">Yes, family plot</option>
                          <option value="no">No</option>
                          <option value="researching">Currently researching</option>
                        </select>
                      </div>

                      {preferences.disposition.cemetery_owned && preferences.disposition.cemetery_owned !== 'no' && (
                        <>
                          <div>
                            <Label>Cemetery name</Label>
                            <Input
                              value={preferences.disposition.cemetery_name}
                              onChange={(e) => updateSection('disposition', 'cemetery_name', e.target.value)}
                              placeholder="Name of cemetery"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label>Plot/space information</Label>
                            <Textarea
                              value={preferences.disposition.cemetery_plot}
                              onChange={(e) => updateSection('disposition', 'cemetery_plot', e.target.value)}
                              placeholder="Section, lot, plot number, deed information"
                              rows={2}
                              className="mt-1"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Cremation Details */}
                  {(preferences.disposition.type === 'cremation' || preferences.disposition.type === 'aquamation') && (
                    <div className="border-t pt-6 mt-6">
                      <h3 className="text-lg font-semibold mb-4">Cremation Specifics</h3>
                      <div className="space-y-4">
                        <div>
                          <Label>Urn preferences</Label>
                          <Textarea
                            value={preferences.disposition.urn_preferences}
                            onChange={(e) => updateSection('disposition', 'urn_preferences', e.target.value)}
                            placeholder="Type of urn, materials, design preferences"
                            rows={2}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>What to do with ashes</Label>
                          <Textarea
                            value={preferences.disposition.ash_disposition}
                            onChange={(e) => updateSection('disposition', 'ash_disposition', e.target.value)}
                            placeholder="Scatter (where?), bury, keep with family, divide among family members, etc."
                            rows={3}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Anatomical Donation */}
                  {preferences.disposition.type === 'anatomical_donation' && (
                    <div className="border-t pt-6 mt-6">
                      <h3 className="text-lg font-semibold mb-4">Anatomical Donation Details</h3>
                      <div className="space-y-4">
                        <div>
                          <Label>Institution preferences</Label>
                          <Textarea
                            value={preferences.disposition.anatomical_donation}
                            onChange={(e) => updateSection('disposition', 'anatomical_donation', e.target.value)}
                            placeholder="Preferred medical schools or research institutions, registration details"
                            rows={3}
                            className="mt-1"
                          />
                        </div>
                        <p className="text-sm text-gray-600">
                          Note: After anatomical donation, cremated remains are typically returned to family after 1-3 years.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Green Burial */}
                  {(preferences.disposition.type === 'green_burial' || preferences.disposition.type === 'terramation') && (
                    <div className="border-t pt-6 mt-6">
                      <h3 className="text-lg font-semibold mb-4">Green/Natural Burial Details</h3>
                      <div className="space-y-4">
                        <div>
                          <Label>Specific preferences</Label>
                          <Textarea
                            value={preferences.disposition.green_burial}
                            onChange={(e) => updateSection('disposition', 'green_burial', e.target.value)}
                            placeholder="Preferred green burial site, shroud vs. biodegradable casket, tree planting, etc."
                            rows={3}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </TabsContent>

            {/* Service Tab */}
            <TabsContent value="service">
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-6">Memorial Service Preferences</h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Service Type</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Type of service</Label>
                        <select
                          value={preferences.service.service_type}
                          onChange={(e) => updateSection('service', 'service_type', e.target.value)}
                          className="w-full border-2 rounded-md px-3 py-2 mt-1"
                        >
                          <option value="">Select...</option>
                          <option value="traditional_funeral">Traditional Funeral Service</option>
                          <option value="memorial_service">Memorial Service (without body present)</option>
                          <option value="celebration_of_life">Celebration of Life</option>
                          <option value="graveside">Graveside Service Only</option>
                          <option value="religious">Religious Service</option>
                          <option value="non_religious">Non-religious/Secular Service</option>
                          <option value="private">Private Family Only</option>
                          <option value="none">No Service</option>
                          <option value="undecided">Not decided yet</option>
                        </select>
                      </div>
                      <div>
                        <Label>Timing</Label>
                        <Textarea
                          value={preferences.service.timing}
                          onChange={(e) => updateSection('service', 'timing', e.target.value)}
                          placeholder="Within days of death, after a week, specific day of week, time of day preferences"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3">Location & Venue</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Location type</Label>
                        <select
                          value={preferences.service.location_type}
                          onChange={(e) => updateSection('service', 'location_type', e.target.value)}
                          className="w-full border-2 rounded-md px-3 py-2 mt-1"
                        >
                          <option value="">Select...</option>
                          <option value="funeral_home">Funeral Home</option>
                          <option value="church">Church/Temple/Mosque/Religious Venue</option>
                          <option value="outdoor">Outdoor Location</option>
                          <option value="home">Private Home</option>
                          <option value="reception_hall">Reception Hall/Event Space</option>
                          <option value="cemetery">Cemetery Chapel</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <Label>Specific venue name</Label>
                        <Input
                          value={preferences.service.venue_name}
                          onChange={(e) => updateSection('service', 'venue_name', e.target.value)}
                          placeholder="Name of church, funeral home, park, etc."
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Venue address</Label>
                        <Input
                          value={preferences.service.venue_address}
                          onChange={(e) => updateSection('service', 'venue_address', e.target.value)}
                          placeholder="Street address, city, state"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Accessibility considerations</Label>
                        <Textarea
                          value={preferences.service.accessibility_needs}
                          onChange={(e) => updateSection('service', 'accessibility_needs', e.target.value)}
                          placeholder="Wheelchair access, parking, elderly attendees considerations"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3">Attendance & Atmosphere</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Who should attend?</Label>
                        <select
                          value={preferences.service.attendance}
                          onChange={(e) => updateSection('service', 'attendance', e.target.value)}
                          className="w-full border-2 rounded-md px-3 py-2 mt-1"
                        >
                          <option value="">Select...</option>
                          <option value="immediate_family">Immediate Family Only</option>
                          <option value="family_close_friends">Family and Close Friends</option>
                          <option value="open_to_all">Open to All Who Wish to Attend</option>
                          <option value="invitation_only">Invitation Only</option>
                        </select>
                      </div>
                      <div>
                        <Label>Dress code</Label>
                        <Input
                          value={preferences.service.dress_code}
                          onChange={(e) => updateSection('service', 'dress_code', e.target.value)}
                          placeholder="Traditional dark colors, casual, bright colors, no preference"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3">Clergy & Religious Elements</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Clergy preference</Label>
                        <select
                          value={preferences.service.clergy_preference}
                          onChange={(e) => updateSection('service', 'clergy_preference', e.target.value)}
                          className="w-full border-2 rounded-md px-3 py-2 mt-1"
                        >
                          <option value="">Select...</option>
                          <option value="specific_clergy">Specific Clergy Member</option>
                          <option value="any_clergy">Any Clergy from My Faith Tradition</option>
                          <option value="interfaith">Interfaith/Multi-faith</option>
                          <option value="secular_celebrant">Secular Celebrant/Officiant</option>
                          <option value="family_led">Family-Led (No Clergy)</option>
                          <option value="no_preference">No Preference</option>
                        </select>
                      </div>

                      {preferences.service.clergy_preference === 'specific_clergy' && (
                        <>
                          <div>
                            <Label>Clergy name</Label>
                            <Input
                              value={preferences.service.clergy_name}
                              onChange={(e) => updateSection('service', 'clergy_name', e.target.value)}
                              placeholder="Name of preferred clergy member"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label>Clergy contact information</Label>
                            <Input
                              value={preferences.service.clergy_contact}
                              onChange={(e) => updateSection('service', 'clergy_contact', e.target.value)}
                              placeholder="Phone number, email, church/temple"
                              className="mt-1"
                            />
                          </div>
                        </>
                      )}

                      <div>
                        <Label>Religious tradition</Label>
                        <Input
                          value={preferences.service.religious_tradition}
                          onChange={(e) => updateSection('service', 'religious_tradition', e.target.value)}
                          placeholder="Catholic, Jewish, Muslim, Buddhist, Hindu, Non-denominational Christian, etc."
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Specific rituals or customs</Label>
                        <Textarea
                          value={preferences.service.specific_rituals}
                          onChange={(e) => updateSection('service', 'specific_rituals', e.target.value)}
                          placeholder="Specific prayers, readings, rituals, or customs you want included"
                          rows={3}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </TabsContent>

            {/* Music Tab */}
            <TabsContent value="music">
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-6">Music Preferences</h2>
                <p className="text-sm text-gray-600 mb-6">Music can provide comfort and reflect your personality</p>
                <div className="space-y-6">
                  <div>
                    <Label>Ceremony songs (specific songs for the service)</Label>
                    <Textarea
                      value={preferences.music.ceremony_songs}
                      onChange={(e) => updateSection('music', 'ceremony_songs', e.target.value)}
                      placeholder="List specific songs with artist names. Include when they should be played (processional, reflection, recessional). Can include hymns, contemporary songs, classical pieces, etc."
                      rows={6}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-600 mt-1">Example: 'Amazing Grace' (processional), 'What a Wonderful World' by Louis Armstrong (reflection), 'Somewhere Over the Rainbow' (recessional)</p>
                  </div>

                  <div>
                    <Label>Background music (before/after service, during visitation)</Label>
                    <Textarea
                      value={preferences.music.background_music}
                      onChange={(e) => updateSection('music', 'background_music', e.target.value)}
                      placeholder="Genre preferences, playlist suggestions, mood (peaceful, uplifting, etc.)"
                      rows={4}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Live music preferences</Label>
                    <Textarea
                      value={preferences.music.live_music}
                      onChange={(e) => updateSection('music', 'live_music', e.target.value)}
                      placeholder="Solo vocalist, organist, guitarist, choir, string quartet, bagpipes, etc."
                      rows={3}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Musician contact information (if you have specific musicians in mind)</Label>
                    <Textarea
                      value={preferences.music.musicians_contact}
                      onChange={(e) => updateSection('music', 'musicians_contact', e.target.value)}
                      placeholder="Names and contact info for preferred musicians"
                      rows={2}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Songs to avoid</Label>
                    <Textarea
                      value={preferences.music.songs_to_avoid}
                      onChange={(e) => updateSection('music', 'songs_to_avoid', e.target.value)}
                      placeholder="Any songs you specifically do NOT want played"
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                </div>
              </Card>
            </TabsContent>

            {/* Visitation Tab */}
            <TabsContent value="visitation">
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-6">Visitation & Reception Preferences</h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Visitation/Viewing</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Visitation preference</Label>
                        <select
                          value={preferences.visitation.visitation_preference}
                          onChange={(e) => updateSection('visitation', 'visitation_preference', e.target.value)}
                          className="w-full border-2 rounded-md px-3 py-2 mt-1"
                        >
                          <option value="">Select...</option>
                          <option value="traditional_visitation">Traditional Visitation (Evening Before Service)</option>
                          <option value="same_day">Visitation Same Day as Service</option>
                          <option value="private_family">Private Family Viewing Only</option>
                          <option value="no_visitation">No Visitation</option>
                          <option value="undecided">Not decided yet</option>
                        </select>
                      </div>

                      <div>
                        <Label>Visitation timing</Label>
                        <Input
                          value={preferences.visitation.visitation_timing}
                          onChange={(e) => updateSection('visitation', 'visitation_timing', e.target.value)}
                          placeholder="Duration preferences (e.g., 2-4pm and 6-8pm)"
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label>Visitation location</Label>
                        <Input
                          value={preferences.visitation.visitation_location}
                          onChange={(e) => updateSection('visitation', 'visitation_location', e.target.value)}
                          placeholder="Funeral home, church, private home"
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label>Casket preference</Label>
                        <select
                          value={preferences.visitation.open_closed_casket}
                          onChange={(e) => updateSection('visitation', 'open_closed_casket', e.target.value)}
                          className="w-full border-2 rounded-md px-3 py-2 mt-1"
                        >
                          <option value="">Select...</option>
                          <option value="open">Open Casket</option>
                          <option value="closed">Closed Casket</option>
                          <option value="family_only">Open for Family, Closed for Others</option>
                          <option value="no_casket">No Casket (cremation before service)</option>
                        </select>
                      </div>

                      <div>
                        <Label>Viewing restrictions</Label>
                        <Textarea
                          value={preferences.visitation.viewing_restrictions}
                          onChange={(e) => updateSection('visitation', 'viewing_restrictions', e.target.value)}
                          placeholder="Any restrictions on who can attend visitation, photography rules, etc."
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3">Reception/Gathering</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Reception preference</Label>
                        <select
                          value={preferences.visitation.reception_preference}
                          onChange={(e) => updateSection('visitation', 'reception_preference', e.target.value)}
                          className="w-full border-2 rounded-md px-3 py-2 mt-1"
                        >
                          <option value="">Select...</option>
                          <option value="formal_reception">Formal Reception/Luncheon</option>
                          <option value="casual_gathering">Casual Gathering</option>
                          <option value="graveside_only">Graveside Only (No Reception)</option>
                          <option value="no_reception">No Reception</option>
                        </select>
                      </div>

                      <div>
                        <Label>Reception location</Label>
                        <Input
                          value={preferences.visitation.reception_location}
                          onChange={(e) => updateSection('visitation', 'reception_location', e.target.value)}
                          placeholder="Church hall, restaurant, private home, etc."
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label>Food & beverage preferences</Label>
                        <Textarea
                          value={preferences.visitation.reception_food}
                          onChange={(e) => updateSection('visitation', 'reception_food', e.target.value)}
                          placeholder="Catered meal, potluck, light refreshments, specific foods to include/avoid, alcohol preferences"
                          rows={3}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </TabsContent>

            {/* Memorial Preferences Tab */}
            <TabsContent value="memorial">
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-6">Memorial Preferences</h2>
                <div className="space-y-6">
                  <div>
                    <Label>Flowers</Label>
                    <Textarea
                      value={preferences.memorial_preferences.flowers}
                      onChange={(e) => updateSection('memorial_preferences', 'flowers', e.target.value)}
                      placeholder="Flower preferences, favorite flowers, colors to include/avoid. Or 'In lieu of flowers...'"
                      rows={3}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Donations in lieu of flowers</Label>
                    <Textarea
                      value={preferences.memorial_preferences.donations_lieu}
                      onChange={(e) => updateSection('memorial_preferences', 'donations_lieu', e.target.value)}
                      placeholder="Specific charities or causes. Include full charity name and how to donate."
                      rows={3}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Memorial contributions</Label>
                    <Textarea
                      value={preferences.memorial_preferences.memorial_contributions}
                      onChange={(e) => updateSection('memorial_preferences', 'memorial_contributions', e.target.value)}
                      placeholder="Memorial fund, scholarship fund, tree planting, bench dedication, etc."
                      rows={2}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Photo display preferences</Label>
                    <Textarea
                      value={preferences.memorial_preferences.photo_display}
                      onChange={(e) => updateSection('memorial_preferences', 'photo_display', e.target.value)}
                      placeholder="Photo boards, slideshow preferences, eras to highlight, arrangement preferences"
                      rows={3}
                      className="mt-1"
                    />
                  </div>

                  <div className="border-t pt-4">
                    <Label>Upload photos for memorial display</Label>
                    <p className="text-xs text-gray-600 mb-2">Upload photos for photo boards, slideshows, or display at the service</p>
                    <div className="mt-2">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => handlePhotoUpload(e, 'photoBoard')}
                        disabled={uploading}
                        className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
                      />
                    </div>
                    {uploading && (
                      <p className="text-sm text-blue-600 mt-2">Uploading photos...</p>
                    )}
                    <div className="mt-2">
                      <Textarea
                        value={preferences.photo_board?.photo_notes || ''}
                        onChange={(e) => updateSection('photo_board', 'photo_notes', e.target.value)}
                        placeholder="Notes about specific photos (names, dates, locations, special memories)"
                        rows={3}
                        className="mt-1"
                      />
                    </div>
                    {uploadedPhotos.photoBoard.length > 0 && (
                      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                        {uploadedPhotos.photoBoard.map(photo => (
                          <div key={photo.id} className="relative group">
                            <img
                              src={photo.url}
                              alt={photo.name}
                              className="w-full h-32 object-cover rounded-lg border-2 border-gray-300"
                            />
                            <button
                              onClick={() => removePhoto('photoBoard', photo.id)}
                              className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              type="button"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <Label>Memory sharing</Label>
                    <Textarea
                      value={preferences.memorial_preferences.memory_sharing}
                      onChange={(e) => updateSection('memorial_preferences', 'memory_sharing', e.target.value)}
                      placeholder="Open mic for sharing memories, scheduled speakers only, memory cards for written memories"
                      rows={2}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Video tribute</Label>
                    <Textarea
                      value={preferences.memorial_preferences.video_tribute}
                      onChange={(e) => updateSection('memorial_preferences', 'video_tribute', e.target.value)}
                      placeholder="Pre-made video, slideshow with music, specific videos to include"
                      rows={2}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Guest book</Label>
                    <select
                      value={preferences.memorial_preferences.guest_book}
                      onChange={(e) => updateSection('memorial_preferences', 'guest_book', e.target.value)}
                      className="w-full border-2 rounded-md px-3 py-2 mt-1"
                    >
                      <option value="">Select...</option>
                      <option value="traditional">Traditional Guest Book</option>
                      <option value="memory_book">Memory Book (for written memories)</option>
                      <option value="photo_guestbook">Photo Guest Book</option>
                      <option value="online_only">Online Only</option>
                      <option value="none">No Guest Book</option>
                    </select>
                  </div>
                </div>
              </Card>
            </TabsContent>

            {/* Pre-arrangements Tab */}
            <TabsContent value="prearrangements">
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-6">Funeral Pre-arrangements</h2>
                <p className="text-sm text-gray-600 mb-6">Document any arrangements you've already made</p>
                <div className="space-y-6">
                  <div>
                    <Label>Have you pre-arranged your funeral?</Label>
                    <select
                      value={preferences.prearrangements.prearranged}
                      onChange={(e) => updateSection('prearrangements', 'prearranged', e.target.value)}
                      className="w-full border-2 rounded-md px-3 py-2 mt-1"
                    >
                      <option value="">Select...</option>
                      <option value="fully_prearranged">Yes, Fully Pre-arranged and Prepaid</option>
                      <option value="prearranged_not_paid">Yes, Pre-arranged but Not Prepaid</option>
                      <option value="partial">Partially Pre-arranged</option>
                      <option value="no">No</option>
                      <option value="in_progress">Currently Working on It</option>
                    </select>
                  </div>

                  {preferences.prearrangements.prearranged && preferences.prearrangements.prearranged !== 'no' && (
                    <>
                      <div>
                        <Label>Funeral home name</Label>
                        <Input
                          value={preferences.prearrangements.funeral_home}
                          onChange={(e) => updateSection('prearrangements', 'funeral_home', e.target.value)}
                          placeholder="Name of funeral home"
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label>Funeral home contact</Label>
                        <Input
                          value={preferences.prearrangements.funeral_home_contact}
                          onChange={(e) => updateSection('prearrangements', 'funeral_home_contact', e.target.value)}
                          placeholder="Phone number, address, funeral director name"
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label>Contract/policy number</Label>
                        <Input
                          value={preferences.prearrangements.contract_number}
                          onChange={(e) => updateSection('prearrangements', 'contract_number', e.target.value)}
                          placeholder="Contract or policy number"
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label>Prepaid?</Label>
                        <select
                          value={preferences.prearrangements.prepaid}
                          onChange={(e) => updateSection('prearrangements', 'prepaid', e.target.value)}
                          className="w-full border-2 rounded-md px-3 py-2 mt-1"
                        >
                          <option value="">Select...</option>
                          <option value="fully_paid">Fully Paid</option>
                          <option value="payment_plan">On Payment Plan</option>
                          <option value="insurance_funded">Insurance Funded</option>
                          <option value="not_prepaid">Not Prepaid</option>
                        </select>
                      </div>

                      <div>
                        <Label>Insurance policy information</Label>
                        <Textarea
                          value={preferences.prearrangements.insurance_policy}
                          onChange={(e) => updateSection('prearrangements', 'insurance_policy', e.target.value)}
                          placeholder="Life insurance, burial insurance, policy numbers, company names"
                          rows={3}
                          className="mt-1"
                        />
                      </div>
                    </>
                  )}

                  <div>
                    <Label>Document location</Label>
                    <Textarea
                      value={preferences.prearrangements.documents_location}
                      onChange={(e) => updateSection('prearrangements', 'documents_location', e.target.value)}
                      placeholder="Where are pre-arrangement contracts, cemetery deeds, insurance policies stored?"
                      rows={3}
                      className="mt-1"
                    />
                  </div>
                </div>
              </Card>
            </TabsContent>

            {/* Obituary Tab */}
            <TabsContent value="obituary">
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-6">Obituary Preferences</h2>
                <div className="space-y-6">
                  <div>
                    <Label>Obituary draft or key points</Label>
                    <Textarea
                      value={preferences.obituary.draft}
                      onChange={(e) => updateSection('obituary', 'draft', e.target.value)}
                      placeholder="Draft your obituary or list key points: birth/death info, education, career, military service, family members to mention, hobbies, achievements, organizations, how you want to be remembered"
                      rows={10}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Photo for obituary</Label>
                    <Textarea
                      value={preferences.obituary.photo_preference}
                      onChange={(e) => updateSection('obituary', 'photo_preference', e.target.value)}
                      placeholder="Specific photo to use, recent vs. younger photo preference, location of preferred photo"
                      rows={2}
                      className="mt-1"
                    />
                  </div>

                  <div className="border-t pt-4">
                    <Label>Upload obituary photo(s)</Label>
                    <p className="text-xs text-gray-600 mb-2">Upload photos you'd like to be considered for the obituary</p>
                    <div className="mt-2">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => handlePhotoUpload(e, 'obituary')}
                        disabled={uploading}
                        className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
                      />
                    </div>
                    {uploading && (
                      <p className="text-sm text-blue-600 mt-2">Uploading photos...</p>
                    )}
                    {uploadedPhotos.obituary.length > 0 && (
                      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                        {uploadedPhotos.obituary.map(photo => (
                          <div key={photo.id} className="relative group">
                            <img
                              src={photo.url}
                              alt={photo.name}
                              className="w-full h-32 object-cover rounded-lg border-2 border-gray-300"
                            />
                            <button
                              onClick={() => removePhoto('obituary', photo.id)}
                              className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              type="button"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <Label>Publications</Label>
                    <Textarea
                      value={preferences.obituary.publications}
                      onChange={(e) => updateSection('obituary', 'publications', e.target.value)}
                      placeholder="Which newspapers or publications should run the obituary? Local paper, hometown paper, professional journals?"
                      rows={2}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Online memorials</Label>
                    <Textarea
                      value={preferences.obituary.online_memorials}
                      onChange={(e) => updateSection('obituary', 'online_memorials', e.target.value)}
                      placeholder="Funeral home website, Legacy.com, other online memorial preferences"
                      rows={2}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Social media</Label>
                    <Textarea
                      value={preferences.obituary.social_media}
                      onChange={(e) => updateSection('obituary', 'social_media', e.target.value)}
                      placeholder="What to post on social media, who should post it, account handling after death"
                      rows={3}
                      className="mt-1"
                    />
                  </div>
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        )}

        <div className="mt-8 flex justify-center">
          <Button onClick={savePreferences} disabled={saving} size="lg" className="bg-blue-600 hover:bg-blue-700">
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Saving...' : 'Save All Preferences'}
          </Button>
        </div>

        <div className="mt-8">
          <QuickActions position="bottom" />
        </div>
      </div>
    </Layout>
  );
};

export default Memorial;
